import type { BraceletPatternDto, PatternType } from '@miyuki/types';

import { assertWidth, resizeColumns } from './pattern.columns';
import { NO_CHANGES, type PatternEdit } from './pattern.entity';
import { assertPatternType, setPatternType } from './pattern.type';

/** Lo que gobierna el panel de ajustes del diseño. */
export interface DesignSettings {
  readonly columns: number;
  readonly patternType: PatternType;
}

/**
 * Aplica los ajustes del diseño como **un solo acto** (RF-10c).
 *
 * El panel cambia el ancho y el tejido con un único botón «Aplicar». Si cada
 * uno despachara su propia operación, una sola pulsación dejaría dos entradas
 * en el historial y haría falta deshacer dos veces para revertirla: la usuaria
 * hizo una cosa y tendría que deshacer dos. Por eso el parche inverso es uno
 * solo y apunta al patrón **previo a ambos cambios**.
 *
 * Es el mismo recurso con que `addColorRaisingDeclaredCount` une subir el tope
 * declarado y agregar el color, y por el mismo motivo.
 *
 * Los dos valores se validan **antes** de tocar nada: un ancho imposible no
 * debe dejar el tejido a medio aplicar.
 */
export function applyDesignSettings(
  pattern: BraceletPatternDto,
  settings: DesignSettings,
): PatternEdit {
  assertWidth(settings.columns);
  assertPatternType(settings.patternType);

  const resized = resizeColumns(pattern, settings.columns).pattern;
  const next = setPatternType(resized, settings.patternType).pattern;

  if (next === pattern) return { pattern, undo: NO_CHANGES };

  // Ancho y tejido: ninguno cambia la cantidad de filas (RF-66).
  return { pattern: next, undo: { kind: 'snapshot', pattern, progressDelta: 0 } };
}
